import PropTypes from 'prop-types';
// @mui
import { styled, useTheme } from '@mui/material/styles';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import Stack from '@mui/material/Stack';
// hooks
import { useResponsive } from 'src/hooks/use-responsive';
// components
import Logo from 'src/components/logo';

// ----------------------------------------------------------------------

// Wrapper for the Left side (Form) to ensure it takes full height and centers content independently
const StyledContentWrapper = styled(Stack)(({ theme }) => ({
  width: '100%', // Allows it to take up to max width
  minHeight: '100vh', // Changed from height to minHeight for mobile overflow
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center', // Center vertically
  position: 'relative',
}));

const StyledContent = styled(Stack)(({ theme }) => ({
  width: '100%',
  maxWidth: 480,
  margin: '0 auto',
  paddingLeft: theme.spacing(2),
  paddingRight: theme.spacing(2),
  [theme.breakpoints.up('md')]: {
    paddingLeft: theme.spacing(8),
    paddingRight: theme.spacing(8),
  },
}));

// Wrapper for the Right side (Image) to ensure independent layout
const StyledSection = styled(Stack)(({ theme }) => ({
  flexGrow: 1,
  height: '100vh',
  position: 'relative',
  overflow: 'hidden', // Ensure image doesn't spill
}));

export default function AuthModernLayout({ children, image, rightContent }) {
  const mdUp = useResponsive('up', 'md');

  const renderContent = (
    <StyledContentWrapper>
      <StyledContent>
        <Logo
          sx={{
            mt: { xs: 4, md: 8 },
            mb: { xs: 5, md: 4 },
            mx: { xs: 'auto', md: 0 },
            display: { xs: 'block', md: 'inline-flex' }
          }}
        />

        <Card
          sx={{
            py: { xs: 4, md: 0 },
            px: { xs: 2.5, md: 0 },
            boxShadow: { xs: 3, md: 'none' },
            borderRadius: { xs: 2, md: 0 },
            overflow: { md: 'unset' },
            bgcolor: { md: 'background.default' },
          }}
        >
          {children}
        </Card>
      </StyledContent>
    </StyledContentWrapper>
  );

  const theme = useTheme();

  const isDarkMode = theme.palette.mode === 'dark';

  const renderSection = (
    <StyledSection>
      <Box
        component="img"
        alt="auth"
        src={
          image ||
          (isDarkMode
            ? '/assets/illustrations/hr_login_dark.png'
            : '/assets/illustrations/hr_login_light.png')
        }
        sx={{
          top: 16,
          left: 16,
          objectFit: 'cover',
          position: 'absolute',
          width: 'calc(100% - 32px)',
          height: 'calc(100% - 32px)',
          borderRadius: 2,
        }}
      />
      {rightContent}
    </StyledSection>
  );

  return (
    <Stack
      component="main"
      direction="row"
      sx={{
        minHeight: '100vh',
      }}
    >
      {/* 
        The left side (Content) and right side (Section) are now separate height containers 
        stacked in a row. Changes in one won't affect the other's dimensions.
      */}

      {/* Left Side */}
      <Stack sx={{ flex: { xs: 1, md: '0 0 auto' }, width: { md: 480 + 128 } }}>
        {renderContent}
      </Stack>

      {/* Right Side */}
      {mdUp && renderSection}
    </Stack>
  );
}

AuthModernLayout.propTypes = {
  children: PropTypes.node,
  image: PropTypes.string,
  rightContent: PropTypes.node,
};
